import get from 'lodash/get';
import dayjs from '@/localPlugins/dayjs';
import { stripHtmlToPlainText } from './htmlParser';
import { getCustomerName } from './customerHelper';
import { getFormattedFileSize } from './fileUtils';

const TRANSCRIPT_MESSAGE_TIMESTAMP = 'MM/DD/YYYY hh:mm:ss A';
const TRANSCRIPT_DATE_FORMAT = 'MM/DD/YYYY';
const TRANSCRIPT_GENERATED_TIMESTAMP = 'MMMM D, YYYY hh:mm:ss A';
const TRANSCRIPT_SEPARATOR = '----------------------------------------';
const SYSTEM_ACTION = {
  AGENT_ATTACHED: 'conversations.agents.attach',
  AGENT_DETACHED: 'conversations.agents.detach',
};

export function applyReplyMessages({ messages }) {
  let lastReplyMessage = null;

  for (let i = 0; i < messages.length; i += 1) {
    const message_type = get(messages[i], 'payload.message_type');
    if (message_type === 'replies' || message_type === 'carousel') {
      if (lastReplyMessage) {
        // update selection
        messages[i] = { ...messages[i] };
        messages[i].replyitem = lastReplyMessage.payload.reply;
        lastReplyMessage = null;
      }
      messages[i].disabled = true;
    }

    if (message_type === 'reply') {
      lastReplyMessage = messages[i];
    }
  }
  return messages;
}

export function composeMessage({ files, content, originalContent, translation, message_type }) {
  let message = {};

  if (files && files.length) {
    // files are coming with message structure so just adds them to attachments
    message.rich_text = {
      attachments: [...files],
      content,
      originalContent,
      translation,
    };
    message.message_type = 'rich_text';
  } else if (message_type) {
    // if the message is already structured, let it go
    message = arguments[0]; // eslint-disable-line
  } else {
    message.content = content;
    message.originalContent = originalContent;
    message.translation = translation;
  }

  return message;
}

export function getMessageContent(message) {
  const messageType = get(message, 'payload.message_type', '');
  if (messageType) {
    return get(message, `payload.${messageType}.content`, '') || '';
  }
  return get(message, 'payload.content', '') || get(message, 'message', '') || '';
}

function getAttachmentFileName(attachment) {
  return get(attachment, 'file.filename')
    || get(attachment, 'file.name')
    || get(attachment, 'filename')
    || get(attachment, 'name');
}

function formatAttachmentLine(attachment, { translate, translationPath }) {
  const fileName = getAttachmentFileName(attachment);
  if (!fileName) {
    return '';
  }

  const formattedSize = getFormattedFileSize(attachment);
  const fileSize = formattedSize ? ` (${formattedSize})` : '';

  return translate(`${translationPath}.attachment_uploaded`, {
    fileName,
    fileSize,
    defaultValue: `Attachment uploaded: ${fileName}${fileSize}`,
  });
}

function getAttachmentLines(message, context) {
  const messageType = get(message, 'payload.message_type', '');
  const attachments = get(message, `payload.${messageType}.attachments`, []);

  if (attachments.length) {
    return attachments.map(attachment => formatAttachmentLine(attachment, context)).filter(Boolean);
  }

  if (messageType === 'file') {
    const line = formatAttachmentLine(get(message, 'payload.file', {}), context);
    return line ? [line] : [];
  }

  return [];
}

function isSystemMessage(message) {
  const messageType = get(message, 'payload.message_type', '');
  return messageType === 'system'
    || messageType === 'event'
    || message.channel === 'system';
}

function getAgentDisplayName(agent) {
  if (!agent) {
    return 'Agent';
  }

  const displayName = get(agent, 'profile.display_name', '').trim();
  const fullName = get(agent, 'fullName', '')
    || `${get(agent, 'profile.first_name', '')} ${get(agent, 'profile.last_name', '')}`.trim();

  return displayName || fullName || 'Agent';
}

function getSystemMessageAgentNames(message) {
  const agents = get(message, 'payload.system.data.agents', []);
  return agents
    .map(agent => get(agent, 'display_name') || agent.name)
    .filter(Boolean)
    .join(', ');
}

function getSystemMessageContent(message, { translate, customerId }) {
  const action = get(message, 'payload.system.action', '');
  const rawContent = get(message, 'payload.system.content', '') || get(message, 'payload.content', '');

  if (!translate || !action) {
    return stripHtmlToPlainText(rawContent).trim();
  }

  const agentNames = getSystemMessageAgentNames(message);
  if (action === SYSTEM_ACTION.AGENT_ATTACHED || action === SYSTEM_ACTION.AGENT_DETACHED) {
    const joinedKey = agentNames.split(', ').filter(Boolean).length > 1 ? 'agents_joined' : 'agent_joined';
    const langKey = action === SYSTEM_ACTION.AGENT_ATTACHED ? joinedKey : 'agent_left';
    return translate(`messages.system.${langKey}`, {
      defaultValue: rawContent,
      agents: agentNames,
    });
  }

  const langKey = action.replaceAll('.', '_');
  return translate(`messages.system.${langKey}`, {
    defaultValue: rawContent,
    customerId,
  });
}

function getTranscriptMessageContent(message, context) {
  if (isSystemMessage(message)) {
    return getSystemMessageContent(message, context);
  }

  const text = stripHtmlToPlainText(getMessageContent(message)).trim();
  const attachmentLines = getAttachmentLines(message, context);

  return [text, ...attachmentLines].filter(Boolean).join('\n');
}

function getTranscriptSenderDisplay(message, { customer, agentsById }) {
  if (isSystemMessage(message)) {
    return 'System';
  }

  if (message.sender_type === 'agents') {
    const agent = message.sender || agentsById[message.sender_id];
    return `${getAgentDisplayName(agent)} (Agent)`;
  }

  if (message.sender_type === 'customers') {
    return `${getCustomerName(customer)} (Customer)`;
  }

  return '';
}

function getCallTranscriptMessageContent(message) {
  const call_transcript = get(message, 'payload.call.transcript');
  if (!call_transcript) {
    return '';
  }
  const call_type = get(message, 'payload.call.type');
  try {
    let jsonData = JSON.parse(call_transcript);
    if (Array.isArray(jsonData)) {
      jsonData = jsonData.map((item) => `${get(item, 'speaker')}: ${get(item, 'transcript')}`)
        .join('\n');
    }
    return {
      call_transcript_content: jsonData,
      call_type,
    };
  } catch (error) {
    return {call_transcript_content: call_transcript, call_type};
  }
}

function formatTranscriptMessageBlock(message, context, enableCallTranscript) {
  const content = getTranscriptMessageContent(message, context);
  const {call_transcript_content, call_type} = enableCallTranscript ? getCallTranscriptMessageContent(message, context) : {};
  const senderDisplay = getTranscriptSenderDisplay(message, context);

  if (call_transcript_content && call_transcript_content.length && senderDisplay) {
    const timestamp = dayjs(message.created_at).format(TRANSCRIPT_MESSAGE_TIMESTAMP);
    return `${timestamp} | ${senderDisplay} | Call type: ${call_type}\n${call_transcript_content}`;
  }

  if (!content || !senderDisplay) {
    return '';
  }

  const timestamp = dayjs(message.created_at).format(TRANSCRIPT_MESSAGE_TIMESTAMP);
  return `${timestamp} | ${senderDisplay}\n${content}`;
}

export function enrichMessagesForTranscript(messages = [], { customer, agentsById }) {
  return messages.map(message => {
    const enriched = { ...message };

    if (message.sender_type === 'agents') {
      enriched.sender = agentsById[message.sender_id] || message.sender;
    } else if (message.sender_type === 'customers') {
      enriched.sender = customer;
    }

    return enriched;
  });
}

export function formatMessagesAsTranscriptText(messages = [], {
  startDate,
  endDate,
  customer,
  agentsById = {},
  translate,
  translationPath = 'conversation_tab.side_panel.conversation_transcript_accordion',
  enableCallTranscript = false,
} = {}) {
  const t = (key, options) => translate(`${translationPath}.${key}`, options);
  const context = {
    customer,
    agentsById,
    customerId: get(customer, 'id', ''),
    translate,
    translationPath,
  };

  const messageBlocks = enrichMessagesForTranscript(messages, { customer, agentsById })
    .map(message => formatTranscriptMessageBlock(message, context, enableCallTranscript))
    .filter(Boolean);

  const title = t('support_chat_title');
  const dateRange = t('date_range_value', {
    startDate: dayjs(startDate).format(TRANSCRIPT_DATE_FORMAT),
    endDate: dayjs(endDate).format(TRANSCRIPT_DATE_FORMAT),
    interpolation: { escapeValue: false },
  });
  const messagesText = messageBlocks.join('\n\n');
  const generatedAt = dayjs().format(TRANSCRIPT_GENERATED_TIMESTAMP);
  const generatedLine = t('transcript_generated', {
    generatedAt,
    defaultValue: `Transcript generated: ${generatedAt}`,
  });

  return [
    title,
    dateRange,
    '',
    TRANSCRIPT_SEPARATOR,
    '',
    messagesText,
    '',
    TRANSCRIPT_SEPARATOR,
    '',
    generatedLine,
  ].join('\n');
}

export function appendSenderProfile(message, agents) {
  if (!message || !agents || message.sender) {
    return message;
  }

  if (message.sender_type === 'agents' && agents.find) {
    const matchedAgent = agents.find(agent =>
      // eslint-disable-next-line implicit-arrow-linebreak
      parseInt(agent.id, 10) === parseInt(message.sender_id, 10)) || null;
    message.sender = matchedAgent;
  }
  return message;
}
